import { cva } from 'class-variance-authority';
import { Flex, Text, Avatar, Tooltip } from '@nula/core-ui';
import type { Access, Permission } from '@/lib/sharing/utils/permissions';
import styles from './shared-by-chip.module.css';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const chip = cva(styles.chip);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PERMISSION_LABELS: Record<Permission, string> = {
  read: 'View access',
  edit: 'Edit access',
  admin: 'Admin access',
  owner: '',
};

function formatGrantDate(dateStr: string): string {
  try {
    return new Intl.DateTimeFormat(navigator.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// SharedByChip
//
// Inline chip shown below doc/collection rows in list views when the viewer
// is not the owner. Shows the granter's avatar + "Shared by {name}".
// A tooltip reveals the permission level and grant date.
//
// Returns null when grantedBy is null (owned rows or system-granted access).
// Non-interactive in v1.
// ---------------------------------------------------------------------------

interface SharedByChipProps {
  access: Access;
}

export function SharedByChip({ access }: SharedByChipProps) {
  if (!access.grantedBy) return null;

  const { name, avatarUrl } = access.grantedBy;
  const label = PERMISSION_LABELS[access.permission];
  const dateLabel = formatGrantDate(access.grantedAt);
  const tooltipTitle = `Shared by ${name} · ${label} · granted ${dateLabel}`;

  return (
    <Tooltip title={tooltipTitle}>
      <Flex align="center" gap="xs" className={chip()}>
        <Avatar size={16} src={avatarUrl ?? undefined}>
          {name[0]}
        </Avatar>
        <Text size="xs" type="secondary" ellipsis>
          Shared by {name}
        </Text>
      </Flex>
    </Tooltip>
  );
}
